import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import apiService from '../../services/api.service';
import { generateSapPassword } from '../../utils/sapPassword';

/**
 * "My SAP B1 account": a PRPO user has ONE SAP B1 user for all companies. Everyone sees which SAP
 * user they are linked to and whether PRPO has its password for PO approvals from e-mail. Only
 * administrators (account.canEdit) can link it and save its password here: PRPO checks the user +
 * password with SAP (in one of their companies) before saving, and an expired password can be
 * replaced (SAP ChangePassword). Other users' passwords are set in Settings › SAP Approval
 * Workflows › SAP B1 passwords.
 */
const MySapAccountDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [loading, setLoading] = React.useState(false);
  const [account, setAccount] = React.useState<any | null>(null);
  const [companyId, setCompanyId] = React.useState<number | ''>('');
  const [code, setCode] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [changing, setChanging] = React.useState(false);
  const [newPw, setNewPw] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await apiService.getMySapAccount();
      const d = res?.data || null;
      setAccount(d);
      setCode((c) => c || d?.sapUserCode || d?.saved?.sapUserCode || '');
      if (d?.companies?.length) {
        setCompanyId((cur) => {
          if (cur) return cur;
          const preferred = Number(localStorage.getItem('selectedCompanyId') || 0);
          return (d.companies.find((c: any) => c.id === preferred) || d.companies.find((c: any) => c.sapApproval) || d.companies[0]).id;
        });
      }
    } catch (e: any) {
      setMsg({ severity: 'error', text: String(e?.response?.data?.message || 'Could not read your SAP account') });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (open) {
      setMsg(null); setPassword(''); setChanging(false); setNewPw(''); setCopied(false);
      void load();
    }
  }, [open, load]);

  const linkedCode = String(account?.sapUserCode || '');
  const saved = account?.saved || null;
  const canEdit = account?.canEdit === true;

  const save = async () => {
    if (!companyId) return;
    setSaving(true); setMsg(null);
    try {
      const res: any = await apiService.saveMySapAccount({
        companyId: Number(companyId), sapUserCode: code.trim(), password, ...(changing ? { newPassword: newPw } : {}),
      });
      setMsg({ severity: 'success', text: String(res?.message || 'Saved') });
      setPassword('');
      setChanging(false);
      await load();
    } catch (e: any) {
      setMsg({ severity: 'error', text: String(e?.response?.data?.message || 'Could not save your SAP account') });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setMsg(null);
    try {
      await apiService.deleteMySapAccount();
      await load();
      setMsg({ severity: 'info', text: 'Your saved SAP password was removed. Your next approval will ask for it.' });
    } catch (e: any) {
      setMsg({ severity: 'error', text: String(e?.response?.data?.message || 'Could not remove it') });
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(newPw); setCopied(true); } catch { setCopied(false); }
  };

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="my-sap-account-title">
      <DialogTitle id="my-sap-account-title">My SAP B1 account</DialogTitle>
      <DialogContent dividers>
        {loading && !account ? <CircularProgress size={22} aria-label="Loading" /> : null}
        {account ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {canEdit
                ? 'Your SAP B1 user is the same in every company. Save it with its password once and approve POs from e-mail with a single click, in any company. PRPO checks them with SAP, keeps the password encrypted and never shows it.'
                : 'The SAP B1 user your PRPO user is linked to, the same in every company. Your PRPO administrator sets its password for PO approvals from e-mail; PRPO keeps it encrypted and never shows it.'}
            </Typography>

            <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>SAP B1 user:</strong> {linkedCode || saved?.sapUserCode || <em>not linked yet{canEdit ? '' : ': ask your PRPO administrator'}</em>}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                <Typography variant="body2"><strong>Password:</strong></Typography>
                {saved ? (
                  <>
                    <Chip
                      size="small"
                      color={saved.status === 'Active' ? 'success' : 'warning'}
                      label={saved.status === 'Active' ? 'Saved' : canEdit ? 'SAP no longer accepts it – enter it again' : 'SAP no longer accepts it – ask your PRPO administrator'}
                    />
                    {saved.verifiedCompany ? (
                      <Typography variant="caption" color="text.secondary">last accepted by SAP in {saved.verifiedCompany}</Typography>
                    ) : null}
                    {canEdit ? <Button size="small" color="error" onClick={remove}>Remove</Button> : null}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">not saved{canEdit ? '' : ': ask your PRPO administrator'}</Typography>
                )}
              </Stack>
            </Box>

            {canEdit && account.canSave === false ? (
              <Alert severity="warning">Saving SAP passwords is not available on this server yet.</Alert>
            ) : null}
            {canEdit && !account.companies.length ? <Alert severity="info">You have no companies in PRPO yet, so PRPO cannot check a password with SAP.</Alert> : null}

            {canEdit && account.companies.length && account.canSave !== false ? (
              <Box component="form" onSubmit={(e) => { e.preventDefault(); void save(); }} sx={{ p: 2, border: '1px solid #cbd5e1', borderRadius: 2 }}>
                <Stack spacing={2}>
                  <TextField
                    size="small"
                    label="SAP B1 user"
                    value={code}
                    onChange={(e) => setCode(e.target.value.trim())}
                    required
                    InputProps={{ readOnly: !!linkedCode }}
                    helperText={linkedCode ? 'Linked to your PRPO user; an administrator can change it' : 'Your user code in SAP B1, e.g. CT60004.219'}
                  />
                  <TextField
                    size="small"
                    type="password"
                    label={changing ? 'Current (expired) SAP password' : 'SAP B1 password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <FormControl size="small">
                    <InputLabel id="my-sap-company">Check it with SAP in</InputLabel>
                    <Select labelId="my-sap-company" label="Check it with SAP in" value={companyId} onChange={(e) => setCompanyId(Number(e.target.value))}>
                      {account.companies.map((c: any) => <MenuItem key={c.id} value={c.id}>{c.name} ({c.database})</MenuItem>)}
                    </Select>
                  </FormControl>
                  {changing ? (
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        The new password becomes your SAP password, also when you log in to SAP B1: <strong>copy it first</strong>.
                        SAP changes it in the company chosen above.
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                          size="small"
                          label="New SAP password"
                          value={newPw}
                          onChange={(e) => { setNewPw(e.target.value); setCopied(false); }}
                          autoComplete="new-password"
                          fullWidth
                          inputProps={{ spellCheck: false, style: { fontFamily: 'monospace' } }}
                        />
                        <Button variant="outlined" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
                        <Button onClick={() => { setNewPw(generateSapPassword()); setCopied(false); }}>Suggest another</Button>
                      </Stack>
                      <Box><Link component="button" type="button" onClick={() => setChanging(false)}>Cancel: my password still works</Link></Box>
                    </Stack>
                  ) : (
                    <Box>
                      <Link component="button" type="button" onClick={() => { setChanging(true); setNewPw(generateSapPassword()); setCopied(false); }}>
                        My SAP password has expired: set a new one
                      </Link>
                    </Box>
                  )}
                  <Box>
                    <Button type="submit" variant="contained" disabled={saving || !companyId || !code || !password}>
                      {saving ? 'Checking with SAP…' : changing ? 'Change password and save' : 'Check with SAP and save'}
                    </Button>
                  </Box>
                </Stack>
              </Box>
            ) : null}
            {msg ? <Alert severity={msg.severity} onClose={() => setMsg(null)}>{msg.text}</Alert> : null}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default MySapAccountDialog;
